import React from 'react';
import { Images } from 'utils';
import { SimpleTooltip } from 'components';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { useFormBuilder } from '../formContext';
import {
    SECTION_NAME_MAX_LENGTH,
    SECTION_TITLE_ALIGN,
    SECTION_TITLE_DEFAULTS as TITLE_DEFAULTS,
    SECTION_TITLE_FONT_SIZE as TITLE_FONT_SIZE,
} from '../section';

const TITLE_ALIGNMENTS = [
    { value: SECTION_TITLE_ALIGN.LEFT, label: 'Align left', Icon: FormatAlignLeftIcon },
    { value: SECTION_TITLE_ALIGN.CENTER, label: 'Align center', Icon: FormatAlignCenterIcon },
    { value: SECTION_TITLE_ALIGN.RIGHT, label: 'Align right', Icon: FormatAlignRightIcon },
];

/**
 * The active section's name and settings, shown when a section is selected but
 * no field in it is. Options live on `section.settings` so new ones land here.
 * Changes are recorded in the template history by the save-time diff.
 *
 * `embedded`: rendered under another panel (the progress-report goal grouping),
 * so it drops its own `.properties-wrapper` frame.
 */
export const SectionSettingsProperties = ({ embedded = false }) => {
    const { formList, setFormList, activeSection } = useFormBuilder();
    const sectionIndex = activeSection - 1;
    const section = formList?.sections?.[sectionIndex];

    const updateSection = (update) => {
        setFormList((prev) => {
            const sections = [...(prev?.sections || [])];
            const current = sections[sectionIndex];
            if (!current) return prev;
            sections[sectionIndex] = update(current);
            return { ...prev, sections };
        });
    };

    const handleNameChange = (name) => updateSection((current) => ({ ...current, name }));

    const handleSettingsChange = (field, value) =>
        updateSection((current) => ({ ...current, settings: { ...(current.settings || {}), [field]: value } }));

    const titleStyle = section?.settings?.titleStyle || {};
    const isBold = titleStyle.bold ?? TITLE_DEFAULTS.bold;

    const handleTitleStyleChange = (field, value) => handleSettingsChange('titleStyle', { ...titleStyle, [field]: value });

    const handleFontSizeChange = (raw) => handleTitleStyleChange('fontSize', raw === '' ? undefined : Number(raw));

    // Clamped on blur, not per keystroke — typing "16" passes through "1" on the way.
    const handleFontSizeBlur = () => {
        if (!titleStyle.fontSize) return;
        const size = Math.min(TITLE_FONT_SIZE.max, Math.max(TITLE_FONT_SIZE.min, titleStyle.fontSize));
        if (size !== titleStyle.fontSize) handleTitleStyleChange('fontSize', size);
    };

    return (
        <div className={embedded ? 'form-settings-properties' : 'properties-wrapper form-settings-properties'}>
            <div className="form-settings-panel-header">
                <div>
                    <h3 className="form-settings-panel-title">Section Settings</h3>
                    <p className="form-settings-panel-subtitle">
                        {activeSection}. {section?.name}
                    </p>
                </div>
            </div>

            <div className="form-settings-section">
                <div className="form-settings-field">
                    <label className="form-settings-field-label">Section Name *</label>
                    <input
                        type="text"
                        className="form-settings-field-input"
                        placeholder="Enter section name"
                        maxLength={SECTION_NAME_MAX_LENGTH}
                        value={section?.name || ''}
                        onChange={(e) => handleNameChange(e.target.value)}
                    />
                    {!section?.name?.trim() && <p className="form-settings-field-error">Section name is required.</p>}
                </div>
            </div>

            <div className="properties-divider" />

            <div className="form-settings-section">
                <div className="form-settings-lock-row">
                    <div className="form-settings-lock-info">
                        <div className="flex-align-center section-settings-title-row">
                            <span className="form-settings-lock-title">Hide section title</span>
                            <SimpleTooltip
                                title="Hides this section's title on the filled form and in the PDF. It stays visible here in the builder."
                                placement="top"
                                arrow
                            >
                                <img src={Images.toolInfoIcon} alt="info" className="section-settings-info-icon" />
                            </SimpleTooltip>
                        </div>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!section?.settings?.hideTitle}
                            onChange={(e) => handleSettingsChange('hideTitle', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <p className="form-settings-lock-desc">
                    {section?.settings?.hideTitle
                        ? 'The section title is hidden on the filled form and in the PDF.'
                        : 'The section title is shown on the filled form and in the PDF.'}
                </p>
            </div>

            {!section?.settings?.hideTitle && (
                <div className="form-settings-section">
                    <div className="table-props-section-header">TITLE STYLE</div>
                    <div className="color-picker-wrapper section-settings-color-picker">
                        <span className="option-label section-settings-color-label">Text Color</span>
                        <div className="color-input-container">
                            <input
                                type="color"
                                value={titleStyle.color || TITLE_DEFAULTS.color}
                                onChange={(e) => handleTitleStyleChange('color', e.target.value)}
                            />
                            <span>{titleStyle.color || TITLE_DEFAULTS.color}</span>
                        </div>
                    </div>

                    <div className="flex-align-center section-settings-style-row">
                        <button
                            type="button"
                            title="Bold"
                            className={`table-props-action-btn ${
                                isBold ? 'active' : ''
                            } flex-justify-align-center section-settings-style-btn`}
                            onClick={() => handleTitleStyleChange('bold', !isBold)}
                        >
                            <b>B</b>
                        </button>
                        <button
                            type="button"
                            title="Italic"
                            className={`table-props-action-btn ${
                                titleStyle.italic ? 'active' : ''
                            } flex-justify-align-center section-settings-style-btn`}
                            onClick={() => handleTitleStyleChange('italic', !titleStyle.italic)}
                        >
                            <i>I</i>
                        </button>
                        <button
                            type="button"
                            title="Underline"
                            className={`table-props-action-btn ${
                                titleStyle.underline ? 'active' : ''
                            } flex-justify-align-center section-settings-style-btn`}
                            onClick={() => handleTitleStyleChange('underline', !titleStyle.underline)}
                        >
                            <u>U</u>
                        </button>
                        <input
                            title="Font Size"
                            type="number"
                            min={TITLE_FONT_SIZE.min}
                            max={TITLE_FONT_SIZE.max}
                            placeholder={String(TITLE_DEFAULTS.fontSize)}
                            value={titleStyle.fontSize || ''}
                            onChange={(e) => handleFontSizeChange(e.target.value)}
                            onBlur={handleFontSizeBlur}
                            className="section-settings-font-size"
                        />
                    </div>

                    <div className="flex-align-center section-settings-style-row">
                        {TITLE_ALIGNMENTS.map(({ value, label, Icon }) => (
                            <button
                                key={value}
                                type="button"
                                title={label}
                                className={`table-props-action-btn ${
                                    (titleStyle.align || SECTION_TITLE_ALIGN.LEFT) === value ? 'active' : ''
                                } flex-justify-align-center section-settings-style-btn`}
                                onClick={() => handleTitleStyleChange('align', value)}
                            >
                                <Icon />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
